// Shop sheet view model (IMPLEMENTATION §2.6 "Shop sheet", §2.8, UX §2 Shop sheet / Wireframe C). PURE:
// ONE horizontal carousel row of the revealed cards (query.shopView: an unlocked item shows once cash
// reached its reveal share, or as the goal's target — M4a #3) in category order Deposits · Machines ·
// Logistics · Board (the next expansion); the category chips jump to their first card. A card: icon, name,
// price (red + ETA when unaffordable) and one sub-line: the first-build ghost's needs ("+2× Lead Lump"),
// "pays back ~3m" when it is the chip's best next step (goalView.advice, the model's Δincome), or
// "Paint in Build" for chutes (painted with the Build Chute tool).
import type { IconKey } from '../fx/icons.ts';
import type { BType, ExpansionId, OreId, ShopId } from '../sim/gen/ids.ts';
import { expansions, type GoalAdvice, shopView } from '../sim/query.ts';
import type { SimState } from '../sim/state.ts';
import { fillCopy } from './copy.ts';
import { etaFromSec, etaText, type Fmt, money } from './format.ts';

export type ShopCategory = 'deposits' | 'machines' | 'logistics' | 'board';
export const SHOP_CATEGORIES: readonly ShopCategory[] = [
  'deposits',
  'machines',
  'logistics',
  'board',
];

/** Where a building type is sold (UX §2: Deposits · Machines · Logistics · Board). */
export function categoryOf(type: BType): ShopCategory {
  switch (type) {
    case 'deposit':
      return 'deposits';
    case 'crate':
    case 'chute':
    case 'dock':
    case 'gallery':
      return 'logistics';
    default:
      return 'machines';
  }
}

export interface ShopCardModel {
  /** `deposit.lead` … or `expansion.5x5` for a Board card. */
  key: string;
  cat: ShopCategory;
  item: ShopId | null;
  expansion: ExpansionId | null;
  /** null = the Board card (a UI icon). */
  icon: IconKey | null;
  name: string;
  /** null = sold out. */
  price: string | null;
  affordable: boolean;
  sub: string | null;
  isNew: boolean;
  /** A drag-out / tap-placement buys it (not a chute, not sold out, not an expansion). */
  placeable: boolean;
  wired: boolean;
}

export interface ShopModel {
  cards: ShopCardModel[];
  /** The category chips: only categories with cards, each with the index of its first card. */
  cats: { id: ShopCategory; label: string; first: number }[];
}

/** Seconds until a best-next-step purchase pays for itself (price ÷ the modelled Δincome, milli-$/s). */
export function paybackSec(a: GoalAdvice): number | null {
  return a.option.dIncome > 0 ? Math.ceil((a.price * 1000) / a.option.dIncome) : null;
}

export function shopModel(
  f: Fmt,
  s: SimState,
  fresh: readonly string[],
  advice: GoalAdvice | null,
): ShopModel {
  const d = f.defs;
  const sc = f.copy.shop;
  const byCat: Record<ShopCategory, ShopCardModel[]> = {
    deposits: [],
    machines: [],
    logistics: [],
    board: [],
  };
  for (const v of shopView(s, d)) {
    if (!v.visible) continue;
    const type: BType = v.kind === 'deposit' ? 'deposit' : (v.ref as BType);
    const ore = v.kind === 'deposit' ? (v.ref as OreId) : null;
    let sub: string | null = null;
    if (v.wired) sub = sc.paintInBuild ?? null;
    else if (v.ghost && type !== 'deposit') {
      const reqs = d.buildings[type].ghost;
      if (reqs.length > 0)
        sub = fillCopy(sc.ghostNeeds ?? '+{items}', {
          items: reqs
            .map((r) =>
              fillCopy(f.copy.cards.reqItem ?? '{n}× {name}', {
                n: r.n,
                name: f.names.item(r.fam, r.tier),
              }),
            )
            .join(', '),
        });
    }
    if (sub === null && advice !== null && advice.option.item === v.id) {
      const sec = paybackSec(advice);
      if (sec !== null)
        sub = fillCopy(sc.payback ?? '', { eta: etaText(f.copy, etaFromSec(sec, f.eta)) });
    }
    const affordable = v.price !== null && v.block !== 'noCash';
    if (sub === null && v.price !== null && !affordable)
      sub = etaText(f.copy, etaFromSec(v.etaSec, f.eta));
    const cat = categoryOf(type);
    byCat[cat].push({
      key: v.id,
      cat,
      item: v.id,
      expansion: null,
      icon: { k: 'building', type, ore, level: 1 },
      name: f.names.shop(v.id),
      price: v.price === null ? null : money(f, v.price),
      affordable,
      sub: v.price === null ? (sc.soldOut ?? null) : sub,
      isNew: fresh.includes(v.id),
      placeable: v.price !== null && !v.wired,
      wired: v.wired,
    });
  }
  const next = expansions(s, d).find((e) => e.state === 'next' && e.block !== 'notUnlocked');
  if (next) {
    const size = `${next.x1 - next.x0 + 1}×${next.y1 - next.y0 + 1}`;
    const affordable = next.block === null;
    byCat.board.push({
      key: `expansion.${next.id}`,
      cat: 'board',
      item: null,
      expansion: next.id,
      icon: null,
      name: fillCopy(f.copy.cards.expand ?? '{size}', { size }),
      price: money(f, next.cost),
      affordable,
      sub: affordable ? null : etaText(f.copy, etaFromSec(next.etaSec, f.eta)),
      isNew: false,
      placeable: false,
      wired: false,
    });
  }
  const cards: ShopCardModel[] = [];
  const cats: ShopModel['cats'] = [];
  for (const c of SHOP_CATEGORIES) {
    if (byCat[c].length === 0) continue;
    cats.push({ id: c, label: sc[c] ?? c, first: cards.length });
    cards.push(...byCat[c]);
  }
  return { cards, cats };
}
